import React, { useMemo, useState, useCallback, useEffect } from 'react';
import CardCommon from '@op/components/CardCommon';
import EChartCommon from '@op/components/EChartCommon';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { chartAllColors } from '@op/constants';
import { DBO_EC, DBO_V, DBO_U } from '@op/utils/format';
import useLanguage from '@op/hooks/useLanguage';
import {
  useVersionTimeline,
  type TMetricItem,
  type TUpdateTitleMap,
} from '@op/pages/SteamPerformance/hooks/useVersionTimeline';
import { Segment } from 'tea-component';
import SingleSelectFilter from '@op/components/SelectComponents/SingleSelectFilter';
import { buildViewOptions, CHART_METRIC_OPTIONS } from '../../constants';
import { getVersionConfig } from '../../../config';
import './index.scss';
import { useStableValue } from '@opm/utils/stableKey';
import { useIntl } from 'umi';
import type { LocaleType } from '@op/types/common';
import CustomFollowerInputDropdown from '@op/components/FilterCommon/components/CustomInputDropdown';

/** 根据语言把稳定 key 翻译成展示文案：isEn 优先 en，否则优先 cn，缺失时回退另一种语言或 key */
const formatTitleByLang = (
  key: string,
  titleMap: TUpdateTitleMap,
  isEn: boolean,
): string => {
  const entry = titleMap?.[key];
  if (!entry) return key;
  if (isEn) return entry.en || entry.cn || key;
  return entry.cn || entry.en || key;
};

interface VersionCompareChartProps {
  filter: Record<string, any>;
  title?: string;
}



const VersionCompareChart: React.FC<VersionCompareChartProps> = ({
  filter,
  title = 'Daily Trend'
}) => {
  const { formatMessage, formatOptionsMessage, isEn } = useLanguage();
  const { locale } = useIntl();
  const [metric, setMetric] = useState<TMetricItem>('reviews');
  const [versionSpan, setVersionSpan] = useState('-');

  const versionConfig = useMemo(
    () => getVersionConfig(filter?.edition_unified_id),
    [filter?.edition_unified_id],
  );

  const viewOptions = useMemo(
    () => (versionConfig ? buildViewOptions(versionConfig) : []),
    [versionConfig],
  );

  // 配置游戏视图：默认第二个维度（如 release）；非配置游戏 viewOptions 为空数组时回退 'release'
  const [configView, setConfigView] = useState<string>(
    viewOptions?.[1]?.value || 'release',
  );

  const { filterParams, isConfigGame } = useMemo(() => {
    const isConfigGame = filter.is_dune;
    return {
      filterParams: {
        ...filter,
        group_by: isConfigGame ? configView : undefined,
        metric_item: metric,
        steam_filter_type: 'steam',
      },
      isConfigGame,
    };
  }, [filter, configView, metric]);

  const { chartData: rawChartData, titleMap, loading, maxDays } =
    useVersionTimeline({
      filterParams,
    });
  const chartData = useStableValue(rawChartData);

  // 根据 versionSpan 裁剪 xAxis 和 series 数据
  const slicedChartData = useMemo(() => {
    // '-' 或空 → 全部展示
    if (!versionSpan || versionSpan === '-') return chartData;

    const parts = versionSpan.split('-');
    if (parts.length !== 2) return chartData;

    const start = Number(parts[0]);
    const end = Number(parts[1]);
    if (isNaN(start) || isNaN(end)) return chartData;

    const from = Math.max(0, Math.min(start, end));
    const xAxisLen = chartData?.xAxis?.length ?? 0;
    if (xAxisLen === 0) return chartData;
    const to = Math.min(xAxisLen - 1, Math.max(start, end));

    return {
      ...chartData,
      xAxis: chartData?.xAxis?.slice(from - 1, to),
      series: chartData?.series?.map((item: any) => ({
        ...item,
        data: item?.data?.slice(from - 1, to),
      })),
    };
  }, [chartData, versionSpan]);

  // 用 titleMap + isEn 把稳定 key 翻译成展示文案（en 用 title_en，否则 cn 优先）
  const formatTitle = useCallback(
    (key: string) => formatTitleByLang(key, titleMap, isEn),
    [titleMap, isEn],
  );
  const metricOptions = useMemo(() => formatOptionsMessage([...CHART_METRIC_OPTIONS]), [formatOptionsMessage]);

  const buildOption = ({
    chartData,
    metricText,
    formatTitle,
    isPercent,
  }: {
    chartData: any;
    metricText: string;
    formatTitle: (name: string) => string;
    isPercent: boolean;
  }) => {

    const valueFormatter = isPercent
      ? (v: number) => DBO_V.ratio(v * 100)
      : (v: number) => DBO_V.reviews(v);

    return {
      color: chartAllColors,
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) =>
          formatChartParamsDate({
            isSort: true,
            title: params[0]?.axisValue,
            rightTitle: metricText,
            params,
            listTitleFunc: (item: any) => formatTitle(item.seriesName),
            valueFormat: valueFormatter,
          }),
      },
      xAxis: {
        type: 'category',
        data: chartData.xAxis,
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => DBO_EC.reviewsPercent(value, isPercent),
        },
      },
      series: chartData.series?.map((item: any) => ({
        name: item.name,
        type: 'line',
        data: item.data,
        smooth: false,
      })),
      legend: {
        data: chartData.legend,
        bottom: 0,
        formatter: (name: string) => formatTitle(name),
      },
      grid: {
        top: 20,
      },
    };
  };

  // ECharts 配置
  const option = useMemo(() => {
    if (loading || chartData.isEmpty) return {};

    const metricOption = metricOptions.find((o) => o.value === metric);
    const isPercent = metricOption?.isPercent ?? false;
    const metricText =
      metricOptions.find((o: any) => o.value === metric)?.text ?? '';
    return buildOption({
      chartData: slicedChartData,
      metricText,
      isPercent,
      formatTitle,
    });
  }, [loading, slicedChartData, metricOptions, configView, formatTitle]);

  // 下载逻辑
  const handleDownload = useCallback(() => {
    DBO_U.download({
      key: 'VersionCompareChartDownload',
      fileName: 'Version Comparison',
      filter: {
        ...filter,
        group_by: isConfigGame ? configView : undefined,
        steam_filter_type: 'steam',
      },
      auxiliaryParams: {
        maxDays,
        isConfigGame,
        groupBy: isConfigGame ? configView : undefined,
        versionSpan,
        trendType: filter?.trend_type || 'daily'
      },
    });
  }, [filter, isConfigGame, configView, maxDays, versionSpan]);



  return (
    <CardCommon
      loading={loading}
      hasDownload
      handleDownloadOnClick={handleDownload}
      className="dbo-version-compare-cardCommon"
      title={
        <>
          <div className='tea-card__title'>{formatMessage(title)}</div>
          <div className="dbo-version-compare-chart__metric-switch">
            {isConfigGame && (
              <Segment
                value={configView}
                onChange={(value: string) => setConfigView(value as TConfigViewType)}
                options={formatOptionsMessage(viewOptions)}
              />
            )}

            <SingleSelectFilter
              className="dbo-version-compare-chart__metric-select"
              value={metric}
              placeholder={formatMessage({ id: 'Metrics' })}
              onChange={(value) => setMetric(value as TMetricItem)}
              options={metricOptions}
            />

            {!loading && <CustomFollowerInputDropdown
              className="dbo-version-follower-input-dropdown"
              lang={locale as LocaleType}
              rangeValue={versionSpan}
              setRangeValue={setVersionSpan}
              placeholder={{ 'en-US': 'Range', 'zh-CN': '范围' }}
              maxValue={maxDays}
              minValue={1}
              regex={/^-?\d+$/}
              maxErrorText={formatMessage("Exceeds the maximum span of the currently selected version.")}
              minErrorText={formatMessage("The minimum value must be {num}.", { num: 1 })}
              valueErrorText={formatMessage('Must be an integer.')}
              unit="D"
              showClear={true}
              unitPosition="prefix"
            />}
          </div>
        </>
      }
      empty={!loading && chartData?.isEmpty !== false}
      loadingHeight={350}
      emptyHeight={350}
      logBtnId="VersionCompareChart"
    >
      <div className="dbo-version-compare-chart">
        <EChartCommon
          options={option}
          height={350}
        />
      </div>
    </CardCommon>
  );
};

export default React.memo(VersionCompareChart);
